import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://bobine.live');
const API = `${BACKEND_URL}/api`;

/* ── Étoile dorée Lingot ── */
const Star = ({ filled }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? '#D4AF37' : 'none'} stroke="#D4AF37" strokeWidth="1.5" style={{ flexShrink: 0 }}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

/* ── Carte témoignage style Lingot ── */
const TestimonialCard = ({ t: item }) => (
  <article
    data-testid={`testimonial-card-${item.id}`}
    style={{
      padding: '24px 26px',
      borderRadius: 14,
      background: 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(212,175,55,0.015))',
      border: '1px solid rgba(212,175,55,0.18)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(212,175,55,0.08)',
      display: 'flex', flexDirection: 'column', gap: 12,
      minHeight: 220,
    }}
  >
    <div style={{ display: 'flex', gap: 3 }}>
      {[1, 2, 3, 4, 5].map(i => <Star key={i} filled={i <= item.rating} />)}
    </div>
    <p style={{
      fontFamily: "'Georgia', serif", fontSize: 14, lineHeight: 1.7,
      color: '#F7E6BD', fontStyle: 'italic', margin: 0, flex: 1,
    }}>
      « {item.text} »
    </p>
    <div style={{ borderTop: '1px solid rgba(212,175,55,0.12)', paddingTop: 12 }}>
      <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37', fontWeight: 500, margin: 0, letterSpacing: 0.3 }}>
        {item.name}
      </p>
      {item.location && (
        <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(212,175,55,0.55)', margin: '2px 0 0', fontStyle: 'italic' }}>
          {item.location}
        </p>
      )}
    </div>
  </article>
);

/* ── Modal de soumission ── */
const SubmitModal = ({ isOpen, onClose, onSuccess }) => {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => { setName(""); setLocation(""); setRating(5); setText(""); setDone(false); };

  const submit = async () => {
    if (!name.trim() || text.trim().length < 10) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, { name: name.trim(), location: location.trim() || null, rating, text: text.trim() });
      setDone(true);
      onSuccess && onSuccess();
    } catch (e) {
      // soft fail
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 8,
    background: 'rgba(0,0,0,0.4)', color: '#F7E6BD',
    border: '1px solid rgba(212,175,55,0.25)', fontFamily: "'Georgia', serif",
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div data-testid="testimonial-submit-modal" onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#000000', border: '1px solid rgba(212,175,55,0.35)',
        borderRadius: 14, padding: 32, maxWidth: 520, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.8), inset 0 1px 0 rgba(212,175,55,0.1)',
      }}>
        {done ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ fontFamily: "'Georgia', serif", fontSize: 18, color: '#D4AF37', marginBottom: 12 }}>Merci infiniment</p>
            <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#F7E6BD', fontStyle: 'italic', lineHeight: 1.7, marginBottom: 24 }}>
              Votre témoignage a été reçu et sera bientôt publié sur Bobine, après une lecture attentive.
            </p>
            <button data-testid="testimonial-submit-close" onClick={() => { reset(); onClose(); }} style={{
              padding: '10px 28px', borderRadius: 24, border: '1px solid rgba(212,175,55,0.35)',
              background: 'linear-gradient(135deg, rgba(212,175,55,0.12), rgba(212,175,55,0.04))',
              color: '#D4AF37', fontFamily: "'Georgia', serif", fontSize: 13, cursor: 'pointer', letterSpacing: 0.5,
            }}>Fermer</button>
          </div>
        ) : (
          <>
            <h3 style={{ fontFamily: "'Georgia', serif", fontSize: 20, color: '#D4AF37', margin: '0 0 6px', textAlign: 'center' }}>
              Laisser mon témoignage
            </h3>
            <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(247,230,189,0.7)', textAlign: 'center', fontStyle: 'italic', margin: '0 0 22px' }}>
              Partagez ce que Bobine a changé pour vous. Votre message sera publié après modération.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <input data-testid="testimonial-name" placeholder="Votre prénom et initiale (ex : Marie L.)" value={name} onChange={e => setName(e.target.value)} maxLength={80} style={inputStyle} />
              <input data-testid="testimonial-location" placeholder="Ville, pays (optionnel)" value={location} onChange={e => setLocation(e.target.value)} maxLength={120} style={inputStyle} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(247,230,189,0.7)' }}>Note :</span>
                <div data-testid="testimonial-rating" style={{ display: 'flex', gap: 4 }}>
                  {[1, 2, 3, 4, 5].map(i => (
                    <button key={i} type="button" onClick={() => setRating(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill={i <= rating ? '#D4AF37' : 'none'} stroke="#D4AF37" strokeWidth="1.5">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
              <textarea data-testid="testimonial-text" placeholder="Votre témoignage (10 à 800 caractères)" value={text} onChange={e => setText(e.target.value)} maxLength={800} rows={5} style={{ ...inputStyle, resize: 'vertical', minHeight: 110 }} />
              <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(247,230,189,0.45)', margin: 0, textAlign: 'right' }}>
                {text.length} / 800
              </p>
              <div style={{ display: 'flex', gap: 12, marginTop: 4, justifyContent: 'flex-end' }}>
                <button data-testid="testimonial-cancel" onClick={() => { reset(); onClose(); }} style={{
                  padding: '10px 22px', borderRadius: 24, border: '1px solid rgba(212,175,55,0.2)',
                  background: 'transparent', color: 'rgba(212,175,55,0.6)', fontFamily: "'Georgia', serif", fontSize: 13, cursor: 'pointer',
                }}>Annuler</button>
                <button data-testid="testimonial-submit" disabled={submitting || !name.trim() || text.trim().length < 10} onClick={submit} style={{
                  padding: '10px 28px', borderRadius: 24, border: '1px solid #D4AF37',
                  background: 'linear-gradient(135deg, #D6BB6A 0%, #C9A632 30%, #A8830C 65%, #6B4F0A 100%)',
                  color: '#000', fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 600,
                  cursor: submitting || !name.trim() || text.trim().length < 10 ? 'not-allowed' : 'pointer',
                  opacity: submitting || !name.trim() || text.trim().length < 10 ? 0.5 : 1,
                  letterSpacing: 0.5, boxShadow: '0 4px 14px rgba(212,175,55,0.35)',
                }}>{submitting ? 'Envoi…' : 'Envoyer'}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


/* ── Section publique ── */
const TestimonialsSection = () => {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/testimonials?limit=12`);
      setItems(r.data.items || []);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  if (items.length === 0) return null;

  return (
    <section data-testid="testimonials-section" style={{
      padding: '40px 20px 80px', maxWidth: 1100, margin: '0 auto',
    }}>
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <h2 style={{
          fontFamily: "'Georgia', serif", fontSize: 24, color: '#D4AF37',
          fontWeight: 400, letterSpacing: 1, margin: '0 0 6px',
        }}>
          Ils ont scellé leurs souvenirs
        </h2>
        <p style={{
          fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(247,230,189,0.75)',
          fontStyle: 'italic', margin: 0,
        }}>
          Quelques voix parmi celles qui tissent le fil de Bobine.
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 20,
      }}>
        {items.map(t => <TestimonialCard key={t.id} t={t} />)}
      </div>

      <div style={{ textAlign: 'center', marginTop: 36 }}>
        <button
          data-testid="testimonial-open-modal"
          onClick={() => setShowModal(true)}
          style={{
            padding: '12px 32px', borderRadius: 28,
            background: 'transparent', border: '1px solid rgba(212,175,55,0.4)',
            color: '#D4AF37', fontFamily: "'Georgia', serif", fontSize: 13,
            fontWeight: 500, cursor: 'pointer', letterSpacing: 0.5,
            transition: 'all 0.3s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.08)'; e.currentTarget.style.borderColor = '#D4AF37'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(212,175,55,0.4)'; }}
        >
          Laisser mon témoignage
        </button>
      </div>

      <SubmitModal isOpen={showModal} onClose={() => setShowModal(false)} onSuccess={fetchItems} />
    </section>
  );
};

export default TestimonialsSection;
